import type { GardenerResponse } from '@services/gardeners';

type CopyFn = (en: string, de: string, hr: string) => string;

type TaskGardenerPickerProps = {
	assigneeIds: string[];
	gardeners: GardenerResponse[];
	suggestedAssignees: number;
	disabled?: boolean;
	onChange: (gardenerIds: string[]) => void;
	copy: CopyFn;
};

function gardenerLabel(g: GardenerResponse): string {
	return g.display_name || g.name || g.email || String(g.id);
}

export default function TaskGardenerPicker({
	assigneeIds,
	gardeners,
	suggestedAssignees,
	disabled = false,
	onChange,
	copy
}: TaskGardenerPickerProps) {
	const suggested = Math.max(1, suggestedAssignees);

	const toggle = (id: string) => {
		if (disabled) return;
		const selected = assigneeIds.includes(id);
		if (selected) {
			onChange(assigneeIds.filter((x) => x !== id));
			return;
		}
		onChange([...assigneeIds, id]);
	};

	if (gardeners.length === 0) {
		return <span className="small text-muted">{copy('No team selected', 'Kein Team gewählt', 'Tim nije odabran')}</span>;
	}

	return (
		<div className="d-flex flex-column gap-1" style={{ minWidth: '140px', maxWidth: '240px' }}>
			{suggested > 1 && (
				<div className="text-muted" style={{ fontSize: '0.7rem' }}>
					{copy(
						`Suggested team size: ${suggested}`,
						`Empfohlene Teamgröße: ${suggested}`,
						`Preporučena veličina tima: ${suggested}`
					)}
				</div>
			)}
			<div className="d-flex flex-wrap gap-1">
				{gardeners.map((g) => {
					const id = String(g.id);
					const selected = assigneeIds.includes(id);
					return (
						<button
							key={id}
							type="button"
							className={`btn btn-sm ${selected ? 'btn-success' : 'btn-outline-secondary'}`}
							disabled={disabled}
							onClick={() => toggle(id)}
							title={gardenerLabel(g)}
						>
							{selected && <i className="bi bi-check-lg me-1" aria-hidden />}
							<span className="text-truncate" style={{ maxWidth: '6.5rem' }}>
								{gardenerLabel(g)}
							</span>
						</button>
					);
				})}
			</div>
			{assigneeIds.length === 0 && (
				<span className="small text-muted">{copy('Unassigned', 'Nicht zugewiesen', 'Nije dodijeljeno')}</span>
			)}
		</div>
	);
}
